import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Info,
  Globe,
  Accessibility,
} from "lucide-react";
import { useUserPreferencesStore } from "../store/userPreferencesStore";
import { useLanguageStore, type Language } from "../store/languageStore";
import { analytics } from "../utils/analytics";

interface OnboardingFlowProps {
  onComplete: () => void;
}

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
  const {
    onboarding,
    updateOnboarding,
    accessibility,
    updateAccessibility,
    updateInterface,
  } = useUserPreferencesStore();
  const { selectedLanguage, setSelectedLanguage } = useLanguageStore();
  const [currentStep, setCurrentStep] = useState(onboarding.currentStep || 0);

  const messages = {
    EN: {
      welcome: {
        title: "Welcome to RightNowAid",
        subtitle: "Let's personalize your experience",
        description:
          "We'll help you set up accessibility features and preferences to make RightNowAid work best for you.",
      },
      language: {
        title: "Choose Your Language",
        subtitle: "Select your preferred language",
        description:
          "RightNowAid supports multiple languages to serve diverse communities.",
      },
      accessibility: {
        title: "Accessibility Preferences",
        subtitle: "Customize for your needs",
        description: "Enable features that make RightNowAid easier to use.",
        reducedMotion: "Reduce Motion",
        reducedMotionDesc: "Minimize animations and transitions",
        fontSize: "Text Size",
        screenReader: "Screen Reader Support",
        screenReaderDesc: "Optimized for assistive technologies",
      },
      complete: {
        title: "You're All Set!",
        subtitle: "RightNowAid is ready to help",
        description:
          "You can always change these settings later from the settings menu.",
        getStarted: "Get Started",
        accessSettings: "Access settings anytime from the header",
      },
      navigation: {
        next: "Next",
        previous: "Previous",
        skip: "Skip",
        finish: "Finish Setup",
        setup: "Setup",
      },
    },
    ES: {
      welcome: {
        title: "Bienvenido a RightNowAid",
        subtitle: "Personalicemos tu experiencia",
        description:
          "Te ayudaremos a configurar las funciones de accesibilidad y preferencias para que RightNowAid funcione mejor para ti.",
      },
      language: {
        title: "Elige tu idioma",
        subtitle: "Selecciona tu idioma preferido",
        description:
          "RightNowAid admite múltiples idiomas para servir a comunidades diversas.",
      },
      accessibility: {
        title: "Preferencias de accesibilidad",
        subtitle: "Personaliza según tus necesidades",
        description: "Habilita funciones que hagan RightNowAid más fácil de usar.",
        highContrast: "Modo de alto contraste",
        highContrastDesc: "Visibilidad mejorada con colores realzados",
        reducedMotion: "Reducir movimiento",
        reducedMotionDesc: "Minimizar animaciones y transiciones",
        fontSize: "Tamaño del texto",
        screenReader: "Soporte para lector de pantalla",
        screenReaderDesc: "Optimizado para tecnologías de asistencia",
      },
      complete: {
        title: "¡Ya está todo listo!",
        subtitle: "RightNowAid está listo para ayudar",
        description: "Siempre puedes cambiar estas configuraciones más tarde desde el menú de configuración.",
        getStarted: "Comenzar",
        accessSettings: "Accede a la configuración en cualquier momento desde el encabezado",
      },
      navigation: {
        next: "Siguiente",
        previous: "Anterior",
        skip: "Omitir",
        finish: "Finalizar configuración",
        setup: "Configuración",
      },
    },
    ZH: {
      welcome: {
        title: "欢迎使用 RightNowAid",
        subtitle: "让我们个性化您的体验",
        description: "我们将帮助您设置辅助功能和偏好，使 RightNowAid 最适合您使用。",
      },
      language: {
        title: "选择您的语言",
        subtitle: "选择您偏好的语言",
        description: "RightNowAid 支持多种语言，为不同社区提供服务。",
      },
      accessibility: {
        title: "辅助功能偏好",
        subtitle: "根据您的需求定制",
        description: "启用使 RightNowAid 更易于使用的功能。",
        highContrast: "高对比度模式",
        highContrastDesc: "通过增强的颜色提高可见性",
        reducedMotion: "减少动画",
        reducedMotionDesc: "最小化动画和过渡效果",
        fontSize: "文字大小",
        screenReader: "屏幕阅读器支持",
        screenReaderDesc: "针对辅助技术优化",
      },
      complete: {
        title: "设置完成！",
        subtitle: "RightNowAid가 도움을 드릴 준비가 되었습니다",
        description: "언제든지 설정 메뉴에서 이러한 설정을 변경할 수 있습니다.",
        getStarted: "시작하기",
        accessSettings: "헤더에서 언제든지 설정에 접근",
      },
      navigation: {
        next: "下一步",
        previous: "上一步",
        skip: "跳过",
        finish: "完成设置",
        setup: "设置",
      },
    },
    VI: {
      welcome: {
        title: "Chào mừng đến với RightNowAid",
        subtitle: "Hãy cá nhân hóa trải nghiệm của bạn",
        description: "Chúng tôi sẽ giúp bạn thiết lập các tính năng và tùy chọn trợ năng để RightNowAid hoạt động tốt nhất cho bạn.",
      },
      language: {
        title: "Chọn ngôn ngữ của bạn",
        subtitle: "Chọn ngôn ngữ ưa thích",
        description: "RightNowAid hỗ trợ nhiều ngôn ngữ để phục vụ các cộng đồng đa dạng.",
      },
      accessibility: {
        title: "Tùy chọn trợ năng",
        subtitle: "Tùy chỉnh theo nhu cầu của bạn",
        description: "Bật các tính năng giúp RightNowAid dễ sử dụng hơn.",
        highContrast: "Chế độ tương phản cao",
        highContrastDesc: "Cải thiện khả năng hiển thị với màu sắc nâng cao",
        reducedMotion: "Giảm chuyển động",
        reducedMotionDesc: "Giảm thiểu hoạt ảnh và hiệu ứng chuyển tiếp",
        fontSize: "Kích thước chữ",
        screenReader: "Hỗ trợ trình đọc màn hình",
        screenReaderDesc: "Tối ưu hóa cho công nghệ hỗ trợ",
      },
      complete: {
        title: "Đã hoàn tất!",
        subtitle: "RightNowAid đã sẵn sàng hỗ trợ",
        description: "Bạn có thể thay đổi các cài đặt này sau này từ menu cài đặt.",
        getStarted: "Bắt đầu",
        accessSettings: "Truy cập cài đặt bất cứ lúc nào từ thanh tiêu đề",
      },
      navigation: {
        next: "Tiếp theo",
        previous: "Quay lại",
        skip: "Bỏ qua",
        finish: "Hoàn tất cài đặt",
        setup: "Cài đặt",
      },
    },
    FIL: {
      welcome: {
        title: "Maligayang pagdating sa RightNowAid",
        subtitle: "I-personalize natin ang iyong karanasan",
        description: "Tutulungan ka naming i-setup ang mga accessibility features at preferences para gumana nang maayos ang RightNowAid para sa iyo.",
      },
      language: {
        title: "Pumili ng Iyong Wika",
        subtitle: "Piliin ang iyong gustong wika",
        description: "Sinusuportahan ng RightNowAid ang maraming wika para maglingkod sa iba't ibang komunidad.",
      },
      accessibility: {
        title: "Mga Accessibility Preference",
        subtitle: "I-customize ayon sa iyong pangangailangan",
        description: "I-enable ang mga feature na ginagawang mas madaling gamitin ang RightNowAid.",
        highContrast: "High Contrast Mode",
        highContrastDesc: "Pinahusay na visibility sa mga enhanced colors",
        reducedMotion: "Bawasan ang Motion",
        reducedMotionDesc: "I-minimize ang mga animation at transition",
        fontSize: "Text Size",
        screenReader: "Screen Reader Support",
        screenReaderDesc: "Optimized para sa assistive technologies",
      },
      complete: {
        title: "Tapos na!",
        subtitle: "Handa nang tumulong ang RightNowAid",
        description: "Maaari mong palaging baguhin ang mga setting na ito sa settings menu.",
        getStarted: "Magsimula",
        accessSettings: "I-access ang settings anumang oras mula sa header",
      },
      navigation: {
        next: "Susunod",
        previous: "Nakaraan",
        skip: "Laktawan",
        finish: "Tapusin ang Setup",
        setup: "Setup",
      },
    },
    KO: {
      welcome: {
        title: "RightNowAid에 오신 것을 환영합니다",
        subtitle: "맞춤형 경험을 시작해보세요",
        description: "RightNowAid가 귀하에게 가장 잘 맞도록 접근성 기능과 환경설정을 도와드리겠습니다.",
      },
      language: {
        title: "언어 선택",
        subtitle: "선호하는 언어를 선택하세요",
        description: "RightNowAid는 다양한 커뮤니티를 위해 여러 언어를 지원합니다.",
      },
      accessibility: {
        title: "접근성 환경설정",
        subtitle: "필요에 맞게 맞춤 설정",
        description: "RightNowAid를 더 쉽게 사용할 수 있게 하는 기능을 활성화하세요.",
        highContrast: "고대비 모드",
        highContrastDesc: "향상된 색상으로 가시성 개선",
        reducedMotion: "모션 감소",
        reducedMotionDesc: "애니메이션과 전환 효과 최소화",
        fontSize: "글자 크기",
        screenReader: "화면 읽기 지원",
        screenReaderDesc: "보조 기술에 최적화",
      },
      complete: {
        title: "설정 완료!",
        subtitle: "RightNowAid가 도움을 드릴 준비가 되었습니다",
        description: "언제든지 설정 메뉴에서 이러한 설정을 변경할 수 있습니다.",
        getStarted: "시작하기",
        accessSettings: "헤더에서 언제든지 설정에 접근",
      },
      navigation: {
        next: "다음",
        previous: "이전",
        skip: "건너뛰기",
        finish: "설정 완료",
        setup: "설정",
      },
    },
    AR: {
      welcome: {
        title: "مرحباً بك في RightNowAid",
        subtitle: "دعنا نخصص تجربتك",
        description: "سن帮助你设置辅助功能和偏好，使 RightNowAid 最适合您使用。",
      },
      language: {
        title: "اختر لغتك",
        subtitle: "اختر لغتك المفضلة",
        description: "يدعم RightNowAid لغات متعددة لخدمة المجتمعات المتنوعة.",
      },
      accessibility: {
        title: "تفضيلات إمكانية الوصول",
        subtitle: "تخصيص حسب احتياجاتك",
        description: "تفعيل الميزات التي تجعل RightNowAid أسهل في الاستخدام.",
        highContrast: "وضع التباين العالي",
        highContrastDesc: "تحسين الرؤية بألوان محسنة",
        reducedMotion: "تقليل الحركة",
        reducedMotionDesc: "تقليل الرسوم المتحركة والانتقالات",
        fontSize: "حجم النص",
        screenReader: "دعم قارئ الشاشة",
        screenReaderDesc: "مُحسّن لتقنيات المساعدة",
      },
      complete: {
        title: "لقد انتهيت!",
        subtitle: "RightNowAid جاهز للمساعدة",
        description: "يمكنك دائماً تغيير هذه الإعدادات لاحقاً من قائمة الإعدادات.",
        getStarted: "ابدأ",
        accessSettings: "الوصول إلى الإعدادات في أي وقت من الرأس",
      },
      navigation: {
        next: "التالي",
        previous: "السابق",
        skip: "تخطي",
        finish: "إنهاء الإعداد",
        setup: "الإعداد",
      },
    },
  };

  const currentMessages =
    messages[selectedLanguage as keyof typeof messages] || messages.EN;
  const steps = useMemo(
    () => [
      { key: "welcome", icon: Info, title: currentMessages.welcome.title },
      { key: "language", icon: Globe, title: currentMessages.language.title },
      {
        key: "accessibility",
        icon: Accessibility,
        title: currentMessages.accessibility.title,
      },
      { key: "complete", icon: Check, title: currentMessages.complete.title },
    ],
    [currentMessages]
  );

  useEffect(() => {
    analytics.track("onboarding_step_viewed", "engagement", {
      step: currentStep,
      stepName: steps[currentStep]?.key,
    });
  }, [currentStep, steps]);

  const handleNext = () => {
    const nextStep = currentStep + 1;
    if (nextStep < steps.length) {
      setCurrentStep(nextStep);
      updateOnboarding({ currentStep: nextStep });
      analytics.track("onboarding_step_completed", "engagement", {
        step: currentStep,
        stepName: steps[currentStep]?.key,
      });
    } else {
      updateOnboarding({
        completed: true,
        currentStep: steps.length,
        completedTutorials: ["basic_setup"],
      });
      analytics.track("onboarding_completed", "conversion");
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      updateOnboarding({ currentStep: prevStep });
      analytics.track("onboarding_step_back", "engagement", {
        step: currentStep,
        stepName: steps[currentStep]?.key,
      });
    }
  };

  const handleSkip = () => {
    updateOnboarding({
      completed: true,
      skippedSteps: [steps[currentStep]?.key],
    });
    analytics.track("onboarding_skipped", "engagement", {
      step: currentStep,
      stepName: steps[currentStep]?.key,
    });
    onComplete();
  };

  const renderStepContent = () => {
    const step = steps[currentStep];

    switch (step.key) {
      case "welcome":
        return (
          <div className="text-center space-y-6">
            <Info className="w-20 h-20 mx-auto text-blue-600 dark:text-blue-400 mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              {currentMessages.welcome.title}
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-6">
              {currentMessages.welcome.subtitle}
            </p>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {currentMessages.welcome.description}
            </p>
          </div>
        );

      case "language":
        return (
          <div className="space-y-8">
            <div className="text-center">
              <Globe className="w-20 h-20 mx-auto text-blue-600 dark:text-blue-400 mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                {currentMessages.language.title}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {currentMessages.language.description}
              </p>
            </div>
            <div className="space-y-4 max-w-md mx-auto">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {currentMessages.language.subtitle}
                </label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => {
                    const langCode = e.target.value as Language;
                    setSelectedLanguage(langCode);
                    updateInterface({ language: langCode });
                    analytics.track("language_selected", "engagement", {
                      language: langCode,
                    });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-colors duration-200"
                >
                  <option value="EN">English</option>
                  <option value="ES">Español</option>
                  <option value="ZH">中文</option>
                  <option value="VI">Tiếng Việt</option>
                  <option value="FIL">Filipino</option>
                  <option value="KO">한국어</option>
                  <option value="AR">العربية</option>
                </select>
              </div>
            </div>
          </div>
        );

      case "accessibility":
        return (
          <div className="space-y-8">
            <div className="text-center">
              <Accessibility className="w-20 h-20 mx-auto text-blue-600 dark:text-blue-400 mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                {currentMessages.accessibility.title}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {currentMessages.accessibility.description}
              </p>
            </div>
            <div className="space-y-4 max-w-md mx-auto">
              <label className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-500 dark:hover:border-blue-400 transition-colors duration-200">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    {currentMessages.accessibility.reducedMotion}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {currentMessages.accessibility.reducedMotionDesc}
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={accessibility.reducedMotion}
                  onChange={(e) =>
                    updateAccessibility({ reducedMotion: e.target.checked })
                  }
                  className="w-5 h-5 text-blue-600 dark:text-blue-400 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors duration-200"
                />
              </label>
            </div>
          </div>
        );

      case "complete":
        return (
          <div className="text-center space-y-4">
            <Check className="w-16 h-16 mx-auto text-green-600 dark:text-green-400 mb-4" />
            <h2 className="text-2xl font-bold mb-2">
              {currentMessages.complete.title}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
              {currentMessages.complete.subtitle}
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {currentMessages.complete.description}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {currentMessages.complete.accessSettings}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-lg font-semibold">
              {currentMessages.navigation.setup} ({currentStep + 1}/{steps.length})
            </h1>
            <button
              onClick={handleSkip}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              {currentMessages.navigation.skip}
            </button>
          </div>

          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              {steps.map((step, index) => (
                <div key={step.key} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                      index < currentStep
                        ? "bg-green-500 dark:bg-green-400 text-white"
                        : index === currentStep
                        ? "bg-blue-600 dark:bg-blue-500 text-white ring-4 ring-blue-100 dark:ring-blue-900/30 transform scale-110"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    {index < currentStep ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="relative flex-1 h-0.5 mx-2">
                      <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700" />
                      <div
                        className={`absolute inset-0 bg-blue-600 dark:bg-blue-500 transition-all duration-500 ease-in-out`}
                        style={{
                          width: index < currentStep ? '100%' : 
                                 index === currentStep ? '50%' : '0%'
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">{renderStepContent()}</div>

          <div className="flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed hover:text-gray-800 dark:hover:text-gray-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentMessages.navigation.previous}
            </button>

            <button
              onClick={handleNext}
              className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              {currentStep === steps.length - 1
                ? currentMessages.navigation.finish
                : currentMessages.navigation.next}
              {currentStep < steps.length - 1 && (
                <ArrowRight className="w-4 h-4 ml-2" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
